import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FairUseLeadInfo } from "./fair-use-people-utils"
import type { FairUsePersonIdentity, FairUsePersonRow, FairUseTier } from "@/api/admin/fair-use"

import { AlertCircle, Info } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"

import { currentUtcYearMonth, fairUseInitials, formatSekAmount } from "./fair-use-ops-utils"
import { FairUsePeopleHeaders } from "./fair-use-people-headers"
import { fairUseRowLeadInfo } from "./fair-use-people-utils"
import {
  EMPTY_PEOPLE_FILTERS,
  type PeopleFilters,
  repeatedPeopleCursor,
  selectPeople,
} from "./fair-use-people-view"
import { FairUsePlanSelect } from "./fair-use-plan-select"
import { fairUseDisplayName, fairUseErrorDescription } from "./fair-use-utils"
import {
  useFairUsePeopleInfiniteQuery,
  useSetFairUseUserTierMutation,
} from "../../hooks/queries/fair-use"
import {
  useFairUseAccessGroupMembersQuery,
  useFairUseAccessGroupsQuery,
} from "../../hooks/queries/fair-use-access-groups"
import { useBackgroundPages } from "../../hooks/use-background-pages"
import { useLocalAiLeadsQuery } from "../local-ai-leads-query"

/** The identity cell — avatar, name and the one line that says which person. */
function FairUsePersonCell({ row }: { row: FairUsePersonIdentity }) {
  // Department first, e-mail as the fallback: the point of the line is "which
  // person is this", and an oid never answers that.
  const secondary = row.profile?.department || row.profile?.email

  return (
    <div className="flex items-start gap-3">
      <span
        aria-hidden
        className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
      >
        {fairUseInitials(row)}
      </span>
      <div className="min-w-0">
        <div className="truncate font-semibold" title={row.user}>
          {fairUseDisplayName(row)}
        </div>
        {secondary && <div className="text-muted-foreground truncate text-xs">{secondary}</div>}
      </div>
    </div>
  )
}

/** Lead standing, in one cell: a badge for a lead, a name for everyone led. */
function FairUseLeadCell({ lead }: { lead: FairUseLeadInfo }) {
  const { t } = useTranslation()

  if (lead.isLead) {
    return (
      <Badge variant="secondary">
        {/* `assigned`, never `count` — i18next reserves `count` for plural
            forms, which these five locales do not carry for this key. */}
        {t("adminDashboard.fairUse.users.leadBadge", "Local AI Lead · {{assigned}} assigned", {
          assigned: lead.assignedCount,
        })}
      </Badge>
    )
  }
  if (lead.leadOf.length > 0) {
    return (
      <span className="text-muted-foreground text-xs">
        {t("adminDashboard.fairUse.users.ledBy", "Answers to {{names}}", {
          names: lead.leadOf.map((entry) => entry.name).join(", "),
        })}
      </span>
    )
  }
  return <span className="text-muted-foreground text-xs">—</span>
}

/** This month's spend — or an em dash for somebody who has not spent anything. */
function FairUseSpendCell({ row }: { row: FairUsePersonRow }) {
  const { t, i18n } = useTranslation()

  // An em dash, not "0 SEK": nothing was measured for this person this month,
  // and a zero would read as a figure somebody checked.
  if (!row.has_usage || row.cost_sek == null) {
    return <span className="text-muted-foreground">—</span>
  }
  return (
    <>
      {t("adminDashboard.fairUse.users.spendAmount", "{{amount}} SEK", {
        amount: formatSekAmount(row.cost_sek, i18n.language),
      })}
    </>
  )
}

/**
 * Who has which plan — the people half of Fair use.
 *
 * It lists the tenant's PEOPLE and layers this month's usage on top, which is
 * the inverse of the operations table. That table is a view of the month's
 * rollups and answers "who is spending this month"; this one has to answer
 * "who is on which plan", and a plan can only be assigned to somebody the
 * table shows. Sourcing it from usage meant a person who had spent nothing was
 * unreachable — the one population an admin most often needs to licence.
 *
 * The chips are unchanged and still ask their month questions ("Requests
 * waiting", "Near their budget", "Stopped", "Custom plan"), so they keep
 * answering from the overview: every row they can return has usage by
 * definition. Only the unfiltered view lists the directory.
 *
 * The lead column is cosmetic in both directions: a row the backend could
 * stamp carries its own flags, and a row it could not falls back to the roster
 * this tab has already loaded for the management card below.
 *
 * There is no read-only variant and no capability check of its own: the only
 * thing that mounts this table is the Fair use area's Users & licences tab,
 * and the area returns the locked upsell (or nothing) for every mode below
 * `manage`. A second gate here could only ever be dead — and a dead gate with
 * a passing test is a claim that the surface is defended when nothing is
 * defending it.
 */
export function FairUsePeopleTable() {
  const { t } = useTranslation()

  // Plans are a standing fact, not a month's arithmetic — but spend is, so the
  // table reads the CURRENT month and offers no month picker. The explicit key
  // (rather than an undefined "current") shares the cache with the operations
  // tab.
  const month = useMemo(() => currentUtcYearMonth(), [])
  const [filters, setFilters] = useState<PeopleFilters>({ ...EMPTY_PEOPLE_FILTERS })
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const updateFilters = (next: PeopleFilters) => {
    setFilters(next)
    setPage(0)
  }
  const peopleQuery = useFairUsePeopleInfiniteQuery(month, true, "")
  const groupsQuery = useFairUseAccessGroupsQuery()
  const accessGroups = groupsQuery.data?.groups ?? []
  const membersQuery = useFairUseAccessGroupMembersQuery(accessGroups.map((group) => group.id))
  const rosterQuery = useLocalAiLeadsQuery()
  const tierMutation = useSetFairUseUserTierMutation()
  const activeQuery = peopleQuery
  const firstPage = peopleQuery.data?.pages[0]
  const directoryUnavailable =
    peopleQuery.data?.pages.some((item) => item.directory_unavailable) ?? false
  const pagingStalled = repeatedPeopleCursor(peopleQuery.data?.pages ?? [])
  const groupStalled = repeatedPeopleCursor(membersQuery.data?.pages ?? [])
  useBackgroundPages(peopleQuery, !directoryUnavailable && !pagingStalled)
  useBackgroundPages(membersQuery, accessGroups.length > 0 && !groupStalled)
  const allPeople = useMemo(
    () =>
      Array.from(
        new Map(
          (peopleQuery.data?.pages.flatMap((item) => item.people) ?? []).map((row) => [
            row.user.toLowerCase(),
            row,
          ])
        ).values()
      ),
    [peopleQuery.data]
  )
  const memberPages = membersQuery.data?.pages ?? []
  const members = useMemo(
    () =>
      new Set(
        (membersQuery.data?.pages ?? [])
          .filter((item) => filters.group?.includes(item.group_id))
          .flatMap((item) => item.member_ids)
          .map((id) => id.toLowerCase())
      ),
    [membersQuery.data, filters.group]
  )
  const groupLabels = useMemo(() => {
    const labels = new Map<string, string[]>()
    const names = new Map((groupsQuery.data?.groups ?? []).map((group) => [group.id, group.name]))
    for (const item of membersQuery.data?.pages ?? [])
      for (const id of item.member_ids) {
        const key = id.toLowerCase(),
          existing = labels.get(key) ?? [],
          name = names.get(item.group_id) ?? item.group_id
        if (!existing.includes(name)) labels.set(key, [...existing, name])
      }
    return labels
  }, [membersQuery.data, groupsQuery.data])
  const groupReadError = memberPages.find((item) => item.error)?.error
  const completedGroups = new Set(
    memberPages.filter((item) => item.group_complete).map((item) => item.group_id)
  )
  const groupDataIncomplete =
    groupsQuery.isLoading ||
    groupsQuery.isError ||
    accessGroups.some((group) => !completedGroups.has(group.id)) ||
    membersQuery.isError ||
    groupStalled
  const mappings = rosterQuery.data?.localAiLeads
  const rows = useMemo(
    () => selectPeople(allPeople, filters, { members, leads: mappings, groupLabels }),
    [allPeople, filters, members, mappings, groupLabels]
  )
  const companies = useMemo(
    () => Array.from(new Set(allPeople.map((row) => row.profile?.company ?? ""))).sort(),
    [allPeople]
  )
  const departments = useMemo(
    () => Array.from(new Set(allPeople.map((row) => row.profile?.department ?? ""))).sort(),
    [allPeople]
  )
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize))
  const currentPage = Math.min(page, pageCount - 1)
  const displayedRows = rows.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
  const membershipIncomplete =
    (filters.group?.some((id) => !completedGroups.has(id)) ?? false) ||
    (Boolean(filters.group?.length) && groupsQuery.isError) ||
    (filters.sort.startsWith("group-") && groupDataIncomplete)
  const incomplete =
    peopleQuery.hasNextPage ||
    peopleQuery.isError ||
    directoryUnavailable ||
    pagingStalled ||
    membershipIncomplete

  const handlePlanChange = (row: FairUsePersonIdentity, tier: FairUseTier) => {
    tierMutation.mutate(
      { userId: row.user, tier },
      {
        onSuccess: () =>
          toast.success(t("adminDashboard.fairUse.toastPlanUpdated", "Plan updated")),
        onError: (error: unknown) =>
          toast.error(t("adminDashboard.fairUse.toastPlanError", "Failed to update plan"), {
            description: fairUseErrorDescription(error, t),
          }),
      }
    )
  }

  if (activeQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    )
  }

  // No first page at all: the month failed, and the admin needs the reason
  // rather than an empty table that reads as "nobody works here".
  if (!firstPage) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm" role="alert">
        <AlertCircle className="text-destructive size-4 shrink-0" />
        <span className="text-destructive">
          {t("adminDashboard.fairUse.loadErrorTitle", "Could not load fair use data")}
        </span>
        <span className="text-muted-foreground">
          {fairUseErrorDescription(activeQuery.error, t)}
        </span>
        <Button
          disabled={activeQuery.isFetching}
          size="sm"
          type="button"
          variant="ghost"
          onClick={() => void activeQuery.refetch()}
        >
          {t("adminDashboard.fairUse.retry", "Try again")}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="fair-use-people-table">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p aria-live="polite" className="text-muted-foreground text-sm">
          {t("adminDashboard.peopleDirectory.loaded", "{{count}} users loaded", {
            count: allPeople.length,
          })}
          {peopleQuery.isFetching || membersQuery.isFetching
            ? ` · ${t("adminDashboard.peopleDirectory.loading", "Loading in background…")}`
            : ""}
          {peopleQuery.dataUpdatedAt
            ? ` · ${t("adminDashboard.peopleDirectory.updated", "Last updated {{time}}", { time: new Date(peopleQuery.dataUpdatedAt).toLocaleTimeString() })}`
            : ""}
          {incomplete
            ? ` · ${t("adminDashboard.peopleDirectory.incomplete", "Results are incomplete")}`
            : ""}
        </p>
        <Button
          disabled={peopleQuery.isFetching}
          size="sm"
          variant="outline"
          onClick={() => {
            void peopleQuery.refetch()
            void groupsQuery.refetch()
            if (accessGroups.length > 0) void membersQuery.refetch()
          }}
        >
          {t("common.refresh", "Refresh")}
        </Button>
      </div>
      {(peopleQuery.isError || pagingStalled) && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm"
          role="alert"
        >
          <span>
            {t(
              "adminDashboard.peopleDirectory.loadError",
              "Loading stopped. The users already loaded are still available."
            )}
          </span>
          <Button
            disabled={peopleQuery.isFetching}
            size="sm"
            variant="outline"
            onClick={() =>
              void (peopleQuery.isFetchNextPageError && !pagingStalled
                ? peopleQuery.fetchNextPage()
                : peopleQuery.refetch())
            }
          >
            {t("common.retry", "Retry")}
          </Button>
        </div>
      )}
      {(groupsQuery.isError || groupReadError || membersQuery.isError || groupStalled) && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm"
          role="alert"
        >
          <span>
            {t(
              "adminDashboard.peopleDirectory.groupError",
              "Access-group information is incomplete. Only confirmed memberships can be shown."
            )}{" "}
            {groupReadError ?? fairUseErrorDescription(membersQuery.error ?? groupsQuery.error, t)}
          </span>
          <Button
            disabled={groupsQuery.isFetching || membersQuery.isFetching}
            size="sm"
            variant="outline"
            onClick={() => {
              void groupsQuery.refetch()
              if (accessGroups.length > 0)
                void (membersQuery.isFetchNextPageError && !groupStalled
                  ? membersQuery.fetchNextPage()
                  : membersQuery.refetch())
            }}
          >
            {t("common.retry", "Retry")}
          </Button>
        </div>
      )}
      {peopleQuery.data?.pages.some((item) => item.truncated) && (
        <p className="text-muted-foreground text-sm" role="status">
          {t(
            "adminDashboard.peopleDirectory.usageIncomplete",
            "Some monthly usage data is incomplete."
          )}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Input
          aria-label={t("adminDashboard.fairUse.ops.filters.searchLabel", "Search people")}
          className="h-10 w-full sm:max-w-sm"
          placeholder={t("adminDashboard.fairUse.ops.filters.search", "Search people…")}
          value={filters.search}
          onChange={(event) => updateFilters({ ...filters, search: event.target.value })}
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => updateFilters({ ...EMPTY_PEOPLE_FILTERS, sort: filters.sort })}
        >
          {t("adminDashboard.peopleDirectory.clear", "Clear filters")}
        </Button>
      </div>

      {directoryUnavailable && (
        <div
          className="text-muted-foreground flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
          role="status"
        >
          <Info className="mt-0.5 size-4 shrink-0" />
          <span>
            {t(
              "adminDashboard.fairUse.users.directoryUnavailable",
              "The user directory could not be read, so only people with usage this month are listed."
            )}
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <Table>
          <FairUsePeopleHeaders
            companies={companies}
            departments={departments}
            filters={filters}
            groups={accessGroups}
            onChange={updateFilters}
          />
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell className="text-muted-foreground py-8 text-center" colSpan={7}>
                  {incomplete
                    ? t(
                        "adminDashboard.peopleDirectory.noLoadedMatches",
                        "No matches in the data loaded so far."
                      )
                    : t(
                        "adminDashboard.fairUse.users.empty",
                        "Nobody matches what you are looking for."
                      )}
                </TableCell>
              </TableRow>
            )}
            {displayedRows.map((row) => (
              <TableRow key={row.user}>
                <TableCell className="max-w-[320px] align-top">
                  <FairUsePersonCell row={row} />
                </TableCell>
                <TableCell className="align-top">
                  <FairUsePlanSelect
                    disabled={tierMutation.isPending}
                    row={row}
                    onChange={handlePlanChange}
                  />
                </TableCell>
                <TableCell className="max-w-48 align-top break-words">
                  {row.profile?.company || "—"}
                </TableCell>
                <TableCell className="max-w-48 align-top break-words">
                  {row.profile?.department || "—"}
                </TableCell>
                <TableCell className="max-w-56 align-top break-words">
                  {groupLabels.get(row.user.toLowerCase())?.join(", ") ||
                    (groupDataIncomplete
                      ? t("adminDashboard.columnFilters.unknown", "Unknown")
                      : "—")}
                </TableCell>
                <TableCell className="align-top">
                  {row.is_lead === undefined && mappings === undefined ? (
                    t("adminDashboard.columnFilters.unknown", "Unknown")
                  ) : (
                    <FairUseLeadCell lead={fairUseRowLeadInfo(row, mappings)} />
                  )}
                </TableCell>
                <TableCell className="text-right align-top tabular-nums">
                  <FairUseSpendCell row={row} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
        <span className="text-muted-foreground text-sm">
          {t("adminDashboard.peopleDirectory.matching", "Matching users: {{count}}", {
            count: rows.length,
          })}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              setPageSize(Number(value))
              setPage(0)
            }}
          >
            <SelectTrigger
              aria-label={t("adminDashboard.peopleDirectory.pageSize", "Rows per page")}
              className="w-24"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[25, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            disabled={currentPage === 0}
            size="sm"
            variant="outline"
            onClick={() => setPage(currentPage - 1)}
          >
            {t("adminDashboard.peopleDirectory.previous", "Previous")}
          </Button>
          <span className="text-sm tabular-nums">
            {t("adminDashboard.peopleDirectory.page", "Page {{page}} of {{pages}}", {
              page: currentPage + 1,
              pages: pageCount,
            })}
          </span>
          <Button
            disabled={currentPage + 1 >= pageCount}
            size="sm"
            variant="outline"
            onClick={() => setPage(currentPage + 1)}
          >
            {t("adminDashboard.peopleDirectory.next", "Next")}
          </Button>
        </div>
      </div>
    </div>
  )
}
